import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Button } from 'transfix-ui/components/Button'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import Grid from 'transfix-ui/core/Grid'
import { useModalState } from 'utils/hooks'
import { InboundFileRouterConfigDetails } from './InboundFileRouterConfigDetails'
import { InboundFileRouterConfigsTable } from './InboundFileRouterConfigsTable'
import { useInboundFileRouterConfig } from './hooks/useInboundFileRouterConfig'
import { InboundFileRouterConfigModal } from './modals/InboundFileRouterConfigModal/InboundFileRouterConfigModal'
import { TInboundFileRouterConfigModalSchema } from './schema/InboundFileRouterConfigModalSchema'

const useInboundFileRouterConfigsStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    maxWidth: '100vw',
    display: 'flex',
    overflowX: 'clip',
  },
  button: {
    marginTop: theme.spacing(1),
  },
  text: {
    marginLeft: theme.spacing(1),
  },
}))

export const InboundFileRouterConfigs = () => {
  const { id } = useParams<{ id: string }>()
  const [isSidebarOpen, setIsSidebarOpen] = useState(Boolean(id))
  const history = useHistory()
  const classes = useInboundFileRouterConfigsStyles()

  const { createToggleHandler, modalState } = useModalState({
    dispatchAddModal: false,
  })
  const openAddHandler = createToggleHandler('dispatchAddModal', true)
  const closeAddHandler = createToggleHandler('dispatchAddModal', false)

  const {
    createInboundFileRouterConfig,
    updateInboundFileRouterConfig,
  } = useInboundFileRouterConfig()

  /*
   * When URL param id updates, it sets new state of Sidebar panel
   */
  useEffect(() => {
    setIsSidebarOpen(Boolean(id))
  }, [id])

  const closeDialogHandler = () => {
    setIsSidebarOpen(false)
    history.replace(`/tms-configs/file-routing-configs`)
  }

  const submitHandler = (data: TInboundFileRouterConfigModalSchema) => {
    const isEdit = !!data.id
    const commonParams = {
      connectionType: data?.connectionType,
      port: data?.port,
      disabled: data?.disabled,
      environment: data?.environment,
      username: data?.username,
      host: data?.host,
      secretKey: data?.secretKey,
      pathPrefix: data?.pathPrefix,
      tmsName: data?.tmsName.toLowerCase(),
      shipperCodeJson: JSON.parse(data?.shipperCodeJson),
    }
    if (isEdit) {
      updateInboundFileRouterConfig({
        id: data?.id,
        ...commonParams,
      })
    } else {
      createInboundFileRouterConfig(commonParams)
    }
  }

  return (
    <div className={classes.root}>
      <Box pl={3}>
        <InboundFileRouterConfigModal
          title='Add a new File Routing Configuration'
          onClose={closeAddHandler}
          open={modalState.dispatchAddModal}
          onSubmit={submitHandler}
        />
        <Grid container direction='row' justifyContent='space-between' alignItems='center'>
          <Grid item xs={12} md='auto'>
            <Typography
              variant='h3'
              className={classes.text}
              data-test='tms-file-routing-configurations-title'
              color='primary'
              display='inline'
              component='div'>
              TMS File Routing Configurations
            </Typography>
          </Grid>
          <Grid item xs={12} md='auto'>
            <Button
              color='primary'
              size='small'
              data-test='add-inbound-config-button'
              className={classes.button}
              onClick={openAddHandler}>
              Add New
            </Button>
          </Grid>
        </Grid>
        <Grid
          container
          direction='row'
          alignItems='center'
          data-test='tms-file-routing-configurations-table-grid-container'>
          <Grid item>
            <InboundFileRouterConfigsTable onEdit={submitHandler} />
          </Grid>
        </Grid>
      </Box>
      {isSidebarOpen && <InboundFileRouterConfigDetails onSidebarClose={closeDialogHandler} />}
    </div>
  )
}
